import { useEffect, useState } from "react";
import { toast } from "sonner";
import { WS_BASE, getToken } from "./api";
import { listenForegroundPush, pushEnabledHere } from "./firebase";

const EVENT = "oryntix:event";
export const emitUserEvent = (detail) => window.dispatchEvent(new CustomEvent(EVENT, { detail }));

// Live mode: "ws" (user socket up) · "push" (FCM carries triggers, socket closed on purpose) · "paused" (no FCM, tab hidden)
// · "reconnecting" (socket wanted but down). The socket is NOT permanent: it opens only when wanted (see wantSocket()).
let wsConnected = false;
let mode = "reconnecting";
export const isWsConnected = () => wsConnected;
const setState = (connected, m) => {
  const changed = wsConnected !== connected || mode !== m;
  wsConnected = connected; mode = m;
  if (changed) emitUserEvent({ type: "ws_state", connected, mode: m });
};
export function useWsConnected() {
  const [c, setC] = useState(wsConnected);
  useEffect(() => onUserEvent(["ws_state"], (e) => setC(!!e.connected)), []);
  return c;
}
export function useLiveMode() {
  const [m, setM] = useState(mode);
  useEffect(() => onUserEvent(["ws_state"], (e) => setM(e.mode)), []);
  return m;
}

// Screens that need live progress not carried by push (running tasks) hold the socket open; released → closed after a grace.
let holds = 0;
const HOLD_EVENT = "oryntix:ws-hold";
export function holdUserSocket() {
  holds += 1; window.dispatchEvent(new Event(HOLD_EVENT));
  let released = false;
  return () => { if (released) return; released = true; holds -= 1; window.dispatchEvent(new Event(HOLD_EVENT)); };
}
export function useUserSocketHold(active) {
  useEffect(() => { if (!active) return undefined; return holdUserSocket(); }, [active]);
}

// Coalesce bursts: many events within `ms` → ONE call (trailing edge), and never two in flight for the same key.
const pending = new Map();
export function coalesce(key, fn, ms = 300) {
  const cur = pending.get(key) || { timer: null, busy: false, again: false };
  pending.set(key, cur);
  const fire = async () => {
    cur.timer = null;
    if (cur.busy) { cur.again = true; return; }
    cur.busy = true;
    try { await fn(); } catch (e) {} finally { cur.busy = false; if (cur.again) { cur.again = false; coalesce(key, fn, ms); } }
  };
  clearTimeout(cur.timer); cur.timer = setTimeout(fire, ms);
}

// Trigger = event (WS or FCM), data = GET. Run `fn` on mount, on every listed event (coalesced), once when the socket
// (re)connects and once when the tab becomes visible again (gap fill). No polling interval.
export function useLiveSync(fn, events, _intervalMs, deps = []) {
  useEffect(() => {
    let alive = true;
    const key = `ls:${Math.random().toString(36).slice(2)}`;
    const run = () => { if (alive) coalesce(key, fn); };
    fn();
    const offEv = events && events.length ? onUserEvent(events, run) : () => {};
    const offWs = onUserEvent(["ws_state"], (e) => { if (e.connected) run(); });
    const onVis = () => { if (document.visibilityState === "visible") run(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; offEv(); offWs(); document.removeEventListener("visibilitychange", onVis); };
    /* eslint-disable-next-line */
  }, deps);
}
// Subscribe to per-user realtime events (reminder_due, incoming_call, task_update, message_new, push). Returns unsubscribe.
export function onUserEvent(types, cb) {
  const set = types ? new Set([].concat(types)) : null;
  const h = (e) => { if (!set || set.has(e.detail?.type)) cb(e.detail); };
  window.addEventListener(EVENT, h);
  return () => window.removeEventListener(EVENT, h);
}

// An FCM payload becomes the same event the socket would have delivered (typed) plus a generic "push" event.
const emitPush = (d) => {
  if (!d) return;
  if (d.type && d.type !== "push") emitUserEvent({ ...d });
  emitUserEvent({ ...d, type: "push" });
};

const HIDDEN_GRACE_MS = 60000; // without FCM: the socket is dropped after the tab has been hidden this long

// Owns /api/ws/user while signed in. Policy (see MOBILE_BRIEF §13): with FCM enabled the socket stays closed unless a
// screen holds it; without FCM it is open while the tab is visible and dropped after 60 s hidden. Auto-reconnect with backoff.
export function useUserEvents(active) {
  useEffect(() => {
    if (!active || !getToken()) return;
    let ws = null, stopped = false, delay = 1000, timer = null, ping = null, hiddenTimer = null, hiddenLong = false;
    const fcm = () => pushEnabledHere();
    const wantSocket = () => holds > 0 || (!fcm() && !hiddenLong);
    const idleMode = () => (fcm() ? "push" : "paused");

    const close = () => { clearTimeout(timer); timer = null; clearInterval(ping); const w = ws; ws = null; try { w && w.close(); } catch (e) {} };
    const open = () => {
      if (stopped || !wantSocket() || (ws && ws.readyState <= 1)) return;
      const sock = new WebSocket(`${WS_BASE}/ws/user?token=${getToken()}`); ws = sock;
      sock.onopen = () => { if (ws !== sock) return; delay = 1000; setState(true, "ws"); clearInterval(ping); ping = setInterval(() => { try { sock.readyState === 1 && sock.send("ping"); } catch (e) {} }, 25000); };
      sock.onmessage = (ev) => { try { emitUserEvent(JSON.parse(ev.data)); } catch (e) {} };
      sock.onclose = (ev) => {
        clearInterval(ping);
        if (ws !== sock) return; // superseded or closed on purpose
        ws = null;
        if (ev.code === 4401) { setState(false, idleMode()); return; } /* invalid token: wait for a new sign-in */
        if (stopped || !wantSocket()) { setState(false, idleMode()); return; }
        setState(false, "reconnecting"); timer = setTimeout(open, delay); delay = Math.min(delay * 2, 30000);
      };
    };
    const reconcile = () => {
      if (stopped) return;
      if (wantSocket()) { if (!ws) { setState(false, "reconnecting"); open(); } }
      else if (ws) { close(); setState(false, idleMode()); }
      else setState(false, idleMode());
    };

    const onVis = () => {
      clearTimeout(hiddenTimer);
      if (document.visibilityState === "visible") { hiddenLong = false; delay = 1000; reconcile(); }
      else hiddenTimer = setTimeout(() => { hiddenLong = true; reconcile(); }, HIDDEN_GRACE_MS);
    };
    // the browser's own network state: a dead socket is not reported until a ping fails, so flip immediately on offline/online
    const onOffline = () => { if (ws) { close(); setState(false, "reconnecting"); } };
    const onOnline = () => { delay = 1000; reconcile(); };
    const onHold = () => reconcile();
    const onPushChanged = () => reconcile();
    // a push shown by the service worker while this tab was hidden → same trigger, so the hidden tab refreshes too
    const onSwMessage = (e) => { if (e.data?.oryntix) emitPush(e.data.data); };

    reconcile();
    let unsubPush = () => {};
    listenForegroundPush((d) => { emitPush(d); if (d.title && d.trigger !== "1") toast(d.title, { description: d.body }); }).then((u) => { unsubPush = u || (() => {}); }).catch(() => {});
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("offline", onOffline); window.addEventListener("online", onOnline);
    window.addEventListener(HOLD_EVENT, onHold); window.addEventListener("oryntix:push-changed", onPushChanged);
    navigator.serviceWorker?.addEventListener?.("message", onSwMessage);
    return () => {
      stopped = true; clearTimeout(hiddenTimer); close(); setState(false, "reconnecting");
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("offline", onOffline); window.removeEventListener("online", onOnline);
      window.removeEventListener(HOLD_EVENT, onHold); window.removeEventListener("oryntix:push-changed", onPushChanged);
      navigator.serviceWorker?.removeEventListener?.("message", onSwMessage);
      unsubPush();
    };
  }, [active]);
}
